import { type JSX, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { kalender, type Kalendertermin } from '../api/termine';
import { ApiFehler } from '../api/fehler';
import { heute as heuteImHaus } from '../datum';
import { Meldung } from '../komponenten/Meldung';
import { Planung } from '../komponenten/Planung';
import { Tagesverweis } from '../komponenten/Maskenverweis';
import { Terminfenster } from './Terminfenster';

/**
 * DIE KALENDERANSICHT DER TERMINE (TAG-29).
 *
 * „Wochen- und Monatsübersicht über die Betriebstage mit Auslastungsanzeige
 * und Sprung in den Tagesplan."
 *
 * Zwei Ansichten, weil sie zwei verschiedene Fragen beantworten — seit
 * Auftrag CRM-67 umschaltbar und nicht mehr untereinander (Auftraggeber,
 * 06.10.2026: „die grundsätzliche Auswahl zwischen Kalender und tagen im
 * einzelnen …, nicht die Ansicht untereinander"):
 *
 *   DER MONATSKALENDER beantwortet „an welchen Tagen läuft etwas". Er zeigt
 *   je Tag die Zahl der Termine und die Auslastung als Anteil — und dient
 *   zum Springen, nicht zum Lesen der Einzelheiten.
 *
 *   DIE TAGESLISTE beantwortet „was läuft an diesem Tag". Je Training eine
 *   Zeile, und Anfang und Dauer stehen als Balken auf einer gemeinsamen
 *   Stundenachse: Wer zwei Zeilen übereinander sieht, sieht die Überschneidung,
 *   ohne zwei Uhrzeiten im Kopf zu vergleichen.
 *
 * WARUM DER BALKEN NICHT ALLEIN STEHT (NFA-14, WCAG 1.4.1). Er trägt seine
 * Zeitangabe als Text, und die Zeile nennt sie noch einmal in einer eigenen
 * Spalte. Eine Länge ist eine Anzeige und keine Aussage: Sie ist nicht
 * vorlesbar, verschwindet bei starker Vergrößerung und fehlt im Ausdruck.
 *
 * DIE ZONE STEHT FEST (FRS-01). Der Kalendertag kommt vom Server, die
 * Uhrzeiten werden mit `timeZone: 'Europe/Berlin'` gebildet. Ein Rechner mit
 * anderer Einstellung zeigte sonst einen anderen Tag als der Kollege am
 * Telefon.
 */

/** Das Fenster der Stundenachse. Außerhalb liegende Termine werden gekappt. */
const ACHSE_VON = 6;
const ACHSE_BIS = 22;

const zeitform = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Berlin',
});
const tagform = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Europe/Berlin',
});
const monatform = new Intl.DateTimeFormat('de-DE', {
  month: 'long',
  year: 'numeric',
  timeZone: 'Europe/Berlin',
});

/** Die Stunde eines Zeitpunkts in Ortszeit, als Dezimalwert. */
function stunde(iso: string): number {
  const teile = zeitform.format(new Date(iso)).split(':');
  return Number(teile[0]) + Number(teile[1]) / 60;
}

/** Der erste Tag des Monatsrasters — Montag der Woche, in der der Erste liegt. */
function rasterbeginn(jahr: number, monat: number): Date {
  const erster = new Date(Date.UTC(jahr, monat, 1));
  // getUTCDay: 0 = Sonntag. Das Raster beginnt montags (DIN 1355-1).
  const versatz = (erster.getUTCDay() + 6) % 7;
  erster.setUTCDate(erster.getUTCDate() - versatz);
  return erster;
}

function alsTag(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Der Standort, an dem der Tagesplan aufgeht (Auftrag CRM-67).
 *
 * Der Filter der Maske, wenn einer gesetzt ist. Sonst der Ort, an dem ALLE
 * Termine des Tages liegen — wer auf einen Tag am Contidrom klickt, will
 * dessen Plan sehen und nicht den des Hauptstandorts. Liegen sie an
 * mehreren Orten, entscheidet die Planung selbst (Hauptstandort, 0147).
 */
function standortDesTages(ort: string | undefined, termine: readonly Kalendertermin[]): string | undefined {
  if (ort !== undefined && ort !== '') return ort;
  const orte = new Set(termine.map((t) => t.ort));
  const [einziger] = orte;
  return orte.size === 1 && einziger !== null && einziger !== undefined ? einziger : undefined;
}

export function Terminkalender({
  artikel,
  ort,
  ansicht = 'kalender',
  beiTagesplan,
  werkzeuge,
  oben,
  darfAbsagen = false,
  darfVereinbarungAendern = false,
  darfWartelisteSehen = false,
  beiTerminakte,
  darfOptionBeantworten = false,
}: {
  readonly artikel?: string;
  /** Der Standort (`standort.kennung`), auf den der Kalender sich beschränkt. */
  readonly ort?: string;
  /**
   * Welche der beiden Ansichten steht (Auftrag CRM-67). Die Wahl trifft die
   * umgebende Maske — sie steht dort neben „Liste je Produkt" und in der Adresse.
   */
  readonly ansicht?: 'kalender' | 'tage';
  /**
   * Der Sprung in die Trainingsplanung mit diesem Tag (TAG-29). Fehlt er, darf
   * der Angemeldete den Tagesplan nicht sehen, und das Datum bleibt Text.
   */
  readonly beiTagesplan?: (tag: string, standort: string | undefined) => void;
  /**
   * Werkzeuge der umgebenden Maske (Ansicht, Produkt, Standort) — sie stehen
   * vor dem Blättern in derselben Werkzeugleiste (ÄA-41, Seitentyp Planung).
   */
  readonly werkzeuge?: ReactNode;
  /** Meldungen der umgebenden Maske zwischen Werkzeugleiste und Fläche. */
  readonly oben?: ReactNode;
  readonly darfAbsagen?: boolean;
  /** DAT-08: die Exklusivvereinbarung pflegen — `termin/aendern`. */
  readonly darfVereinbarungAendern?: boolean;
  /** BUC-11 (ÄA-55): wie viele Wartende zum Termin passen — `buchung/sehen`. */
  readonly darfWartelisteSehen?: boolean;
  /** CRM-117 (L6): der Weg in die Terminakte; ohne ihn bleibt das Fenster ohne Verweis. */
  readonly beiTerminakte?: (termin: string) => void;
  readonly darfOptionBeantworten?: boolean;
}): JSX.Element {
  /*
   * DAS TERMINFENSTER (Auftraggeber, 13.09.2026: „jeder Termin auswählbar …
   * ein popup, das alle Informationen zum Termin hält"). Es steht in einer
   * eigenen Datei, weil dieselbe Frage in drei Masken gestellt wird.
   */
  const [offenerTermin, setzeOffenenTermin] = useState<string | null>(null);
  /*
   * HEUTE NACH DER ZONE DES HAUSES (FRS-01), nicht nach UTC: Kurz nach
   * Mitternacht stünde der Kalender sonst am Monatsersten noch im Vormonat.
   */
  const heute = useMemo(() => heuteImHaus(), []);
  const [jahr, setzeJahr] = useState(() => Number(heute.slice(0, 4)));
  const [monat, setzeMonat] = useState(() => Number(heute.slice(5, 7)) - 1);
  const [termine, setzeTermine] = useState<readonly Kalendertermin[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laedt, setzeLaedt] = useState(false);
  /** Der angeklickte Tag. `null` heißt: keiner — dann gilt die Vorgabe unten. */
  const [gewaehlterTag, setzeGewaehltenTag] = useState<string | null>(null);

  /*
   * Geladen wird das ganze RASTER, nicht nur der Monat: Die erste und letzte
   * Zeile zeigen Tage der Nachbarmonate, und ein leerer Kasten neben einem
   * vollen wäre eine Aussage, die nicht stimmt.
   */
  const beginn = useMemo(() => rasterbeginn(jahr, monat), [jahr, monat]);
  const ende = useMemo(() => {
    const d = new Date(beginn);
    d.setUTCDate(d.getUTCDate() + 41);
    return d;
  }, [beginn]);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      setzeTermine(
        await kalender(alsTag(beginn), alsTag(ende), {
          ...(artikel === undefined ? {} : { artikel }),
          ...(ort === undefined || ort === '' ? {} : { ort }),
        }),
      );
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [beginn, ende, artikel, ort]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /** Je Kalendertag die Termine — die Grundlage beider Teile. */
  const jeTag = useMemo(() => {
    const karte = new Map<string, Kalendertermin[]>();
    for (const t of termine) {
      const liste = karte.get(t.tag);
      if (liste) liste.push(t);
      else karte.set(t.tag, [t]);
    }
    return karte;
  }, [termine]);

  const tage = useMemo(() => {
    const alle: Date[] = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(beginn);
      d.setUTCDate(d.getUTCDate() + i);
      alle.push(d);
    }
    return alle;
  }, [beginn]);

  /* Die Tage mit Terminen, aufsteigend — die Tagesliste zeigt nur diese. */
  const belegteTage = useMemo(
    () => [...jeTag.keys()].sort((a, b) => (a < b ? -1 : 1)),
    [jeTag],
  );

  function blaettere(richtung: -1 | 1): void {
    const d = new Date(Date.UTC(jahr, monat + richtung, 1));
    setzeJahr(d.getUTCFullYear());
    setzeMonat(d.getUTCMonth());
    setzeGewaehltenTag(null);
  }

  /*
   * DER TAG DER SEITENLEISTE, AUCH OHNE KLICK (Auftrag CRM-67).
   *
   * Auftraggeber, 06.10.2026: „in der Kalenderansicht soll die spalte rechts
   * auch beim aufruf der Seite mit dem aktuellen tag angezeigt werden."
   *
   * Liegt heute nicht im gezeigten Monat — wer blättert —, ist es der ERSTE
   * TAG MIT TERMINEN in diesem Monat und nicht der Monatserste: Wer blättert,
   * sucht, was dort läuft, und ein leerer Erster beantwortete das mit einer
   * leeren Spalte. Erst ein Monat ganz ohne Termin zeigt den Ersten — leer,
   * aber mit Datum, damit die Spalte nicht verschwindet.
   */
  const monatsschluessel = `${String(jahr)}-${String(monat + 1).padStart(2, '0')}`;
  const vorgabeTag = heute.startsWith(monatsschluessel)
    ? heute
    : (belegteTage.find((t) => t.startsWith(monatsschluessel)) ?? `${monatsschluessel}-01`);
  const tagRechts = gewaehlterTag ?? vorgabeTag;
  const desGewaehltenTages = jeTag.get(tagRechts) ?? [];

  /*
   * DAS BLÄTTERN STEHT IN DER WERKZEUGLEISTE (ÄA-41, Seitentyp Planung):
   * Datum, Standort und Ansicht oben, der Kalender darunter in voller Breite.
   * Knöpfe stehen in ihrer Inhaltsbreite nebeneinander (Vorlage).
   */
  const blaettern = (
    <div className="knopfreihe">
      <button type="button" className="knopf knopf--klein" onClick={() => blaettere(-1)}>
        ← Vormonat
      </button>
      <button
        type="button"
        className="knopf knopf--klein"
        onClick={() => {
          setzeJahr(Number(heute.slice(0, 4)));
          setzeMonat(Number(heute.slice(5, 7)) - 1);
          setzeGewaehltenTag(null);
        }}
      >
        Heute
      </button>
      <button type="button" className="knopf knopf--klein" onClick={() => blaettere(1)}>
        Folgemonat →
      </button>
      <span className="feld__hilfe">{laedt ? 'lädt …' : `${String(termine.length)} Termine`}</span>
    </div>
  );

  /*
   * DIE SEITENLEISTE: DER GEWÄHLTE TAG (TAG-29, ÄA-41).
   *
   * Der Klick in den Monat zeigt den Tag hier — Zeit, Training, Belegung,
   * Status. Der Name öffnet das Terminfenster wie in der Tagesliste, das
   * Datum den Tagesplan. Seit Auftrag CRM-67 steht sie immer da, auch an
   * einem Tag ohne Termin: Eine Spalte, die beim Blättern verschwindet und
   * wiederkommt, lässt den Kalender unter der Hand springen.
   */
  const seite =
    ansicht !== 'kalender' ? undefined : (
      <>
        <div className="karte__titel">
          <h2>
            <Tagesverweis
              tag={tagRechts}
              standort={standortDesTages(ort, desGewaehltenTages)}
              beiWahl={beiTagesplan}
            >
              {tagform.format(new Date(`${tagRechts}T12:00:00Z`))}
            </Tagesverweis>
          </h2>
          <small>{desGewaehltenTages.length} Termine</small>
        </div>
        {desGewaehltenTages.length === 0 ? (
          <p className="feld__hilfe">An diesem Tag steht kein Termin.</p>
        ) : (
          <ul className="optionen__stand">
            {desGewaehltenTages.map((t) => {
              const zeit = `${zeitform.format(new Date(t.beginn))} – ${zeitform.format(new Date(t.ende))}`;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    className="knopf knopf--verweis"
                    aria-label={`${zeit} ${t.artikelbezeichnung}`}
                    onClick={() => {
                      setzeOffenenTermin(t.id);
                    }}
                  >
                    {zeit} · {t.artikelbezeichnung}
                  </button>{' '}
                  <span className="hilfe">
                    {t.terminart === 'exklusiv'
                      ? 'exklusiv'
                      : `${String(t.belegt)}/${String(t.hoechstteilnehmer)} belegt`}
                    {t.ort !== null && ` · ${t.ort}`}
                  </span>{' '}
                  <span className={t.status === 'buchbar' ? 'marke marke--gut' : 'marke marke--inaktiv'}>
                    {t.status}
                  </span>
                  {/* ANG-30: Die Option ist noch keine Zusage — in der Achtungsfarbe (ÄA-41). */}
                  {t.optionsanfrage !== null && t.optionsstand !== 'zugesagt' && (
                    <>
                      {' '}
                      <span className="marke marke--warn">Option</span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </>
    );

  return (
    <Planung
      werkzeuge={
        <>
          {werkzeuge}
          {blaettern}
        </>
      }
      seitenBeschriftung="Gewählter Tag"
      {...(seite === undefined ? {} : { seite })}
      oben={
        <>
          {oben}
          {fehler !== null && <Meldung fehler={fehler} />}
        </>
      }
    >
      {/*
        EINE ANSICHT, NICHT ZWEI UNTEREINANDER (Auftrag CRM-67). Die Wahl
        steht in der Werkzeugleiste der umgebenden Maske.
      */}
      {ansicht === 'kalender' && (
        <div className="karte">
          <div className="karte__titel">
            <h2>{monatform.format(new Date(Date.UTC(jahr, monat, 15)))}</h2>
            {/* TAG-29 */}
          </div>

          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle kalender">
              <caption className="nur-vorlesen">
                Monatsübersicht: je Tag die Zahl der Termine und die Auslastung
              </caption>
              <thead>
                <tr>
                  {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((w) => (
                    <th key={w} scope="col">
                      {w}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2, 3, 4, 5].map((woche) => (
                  <tr key={woche}>
                    {tage.slice(woche * 7, woche * 7 + 7).map((d) => {
                      const schluessel = alsTag(d);
                      const desTages = jeTag.get(schluessel) ?? [];
                      const fremderMonat = d.getUTCMonth() !== monat;
                      const plaetze = desTages.reduce((n, t) => n + t.hoechstteilnehmer, 0);
                      const belegt = desTages.reduce((n, t) => n + t.belegt, 0);
                      const klasse = [
                        fremderMonat ? 'kalender__fremd' : '',
                        schluessel === tagRechts ? 'kalender__gewaehlt' : '',
                      ]
                        .filter((k) => k !== '')
                        .join(' ');
                      return (
                        <td
                          key={schluessel}
                          className={klasse === '' ? undefined : klasse}
                          {...(schluessel === heute ? { 'aria-current': 'date' as const } : {})}
                        >
                          {desTages.length === 0 ? (
                            <span className="kalender__tag">{d.getUTCDate()}</span>
                          ) : (
                            <button
                              type="button"
                              className="kalender__knopf"
                              aria-pressed={schluessel === tagRechts}
                              aria-label={`${tagform.format(d)}: ${String(desTages.length)} Termine, ${String(belegt)} von ${String(plaetze)} Plätzen belegt`}
                              onClick={() => setzeGewaehltenTag(schluessel)}
                            >
                              <span className="kalender__tag">{d.getUTCDate()}</span>
                              {/*
                                AUSLASTUNG ALS ZAHL UND ALS BALKEN (TAG-29).
                                Die Zahl steht da, weil ein Balken allein nicht
                                vorlesbar ist (NFA-14).
                              */}
                              <span className="kalender__zahl">
                                {desTages.length} · {belegt}/{plaetze}
                              </span>
                              <span
                                className="kalender__last"
                                style={{
                                  width: plaetze === 0 ? '0%' : `${String(Math.min(100, Math.round((belegt / plaetze) * 100)))}%`,
                                }}
                              />
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {ansicht === 'tage' && (
        <div className="karte">
          <div className="karte__titel">
            <h2>Tage im Einzelnen</h2>
            <small>{belegteTage.length} mit Terminen</small>
          </div>

          {belegteTage.length === 0 && (
            <p className="feld__hilfe">
              In diesem Zeitraum steht kein Termin. Blättern Sie in einen anderen Monat — oder
              legen Sie einen Termin an.
            </p>
          )}

          {/*
            NFA-22: Die Stundenachse ist breit. Sie scrollt in ihrem eigenen
            Kasten, damit die Seite es nicht tut.
          */}
          <div className="tafel--weit">
            {belegteTage.map((tag) => (
              <section
                key={tag}
                className={gewaehlterTag === tag ? 'tagesblock tagesblock--gewaehlt' : 'tagesblock'}
              >
                <h3 className="tagesblock__titel">
                  <Tagesverweis
                    tag={tag}
                    standort={standortDesTages(ort, jeTag.get(tag) ?? [])}
                    beiWahl={beiTagesplan}
                  >
                    {tagform.format(new Date(`${tag}T12:00:00Z`))}
                  </Tagesverweis>
                  <span className="feld__hilfe"> · {(jeTag.get(tag) ?? []).length} Termine</span>
                </h3>

                <div className="tabellenhuelle tabellenhuelle--telefon">
                  <table className="liste__tabelle">
                    <thead>
                      <tr>
                        <th scope="col">Training</th>
                        <th scope="col">Zeit</th>
                        <th scope="col">Art</th>
                        <th scope="col">Belegung</th>
                        <th scope="col">
                          {/* Die Achse trägt ihre Stunden als Beschriftung. */}
                          <span className="achse">
                            {Array.from({ length: ACHSE_BIS - ACHSE_VON + 1 }, (_, i) => (
                              <span key={i} className="achse__marke">
                                {String(ACHSE_VON + i).padStart(2, '0')}
                              </span>
                            ))}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(jeTag.get(tag) ?? []).map((t) => {
                        const von = Math.max(ACHSE_VON, Math.min(ACHSE_BIS, stunde(t.beginn)));
                        const bis = Math.max(von, Math.min(ACHSE_BIS, stunde(t.ende)));
                        const spanne = ACHSE_BIS - ACHSE_VON;
                        const links = ((von - ACHSE_VON) / spanne) * 100;
                        const breite = Math.max(1.5, ((bis - von) / spanne) * 100);
                        const zeit = `${zeitform.format(new Date(t.beginn))} – ${zeitform.format(new Date(t.ende))}`;
                        return (
                          <tr key={t.id}>
                            <td>
                              {/*
                                DER NAME FÜHRT INS FENSTER. Ein eigener Knopf
                                „Details" daneben wäre eine Spalte mehr für
                                dieselbe Handlung — und das Erste, wonach man
                                greift, ist ohnehin der Name.
                              */}
                              <button
                                type="button"
                                className="knopf knopf--verweis"
                                onClick={() => {
                                  setzeOffenenTermin(t.id);
                                }}
                              >
                                {t.artikelbezeichnung}
                              </button>
                              <span className="kanalliste__name"> · {t.artikelnummer}</span>
                              {t.ort !== null && <span className="feld__hilfe"> · {t.ort}</span>}
                            </td>
                            {/* PRF-14-Lesart: die Zahl steht neben dem Bild, nicht darin. */}
                            <td className="zahl">{zeit}</td>
                            <td>
                              {/*
                                Die Terminart als WORT und nicht als Marke —
                                dieselbe Ueberlegung wie in `Termine.tsx`: Zwei
                                Werte desselben Merkmals stehen gleich da, und
                                keiner von beiden wertet. Die Marke daneben
                                traegt den Status, und der wertet.
                              */}
                              {t.terminart === 'exklusiv' ? 'exklusiv' : 'offen'}{' '}
                              <span
                                className={
                                  t.status === 'buchbar' ? 'marke marke--gut' : 'marke marke--inaktiv'
                                }
                              >
                                {t.status}
                              </span>
                              {/*
                                DIE OPTION IST GEKENNZEICHNET (ANG-30, 0198).
  
                                Auftraggeber, 13.09.2026: „diese termine sollen in
                                den normalen terminkalender mit eingetragen werden,
                                aber als option gekennzeichnet werden." Ein
                                Wunschtermin aus einem Angebot steht hier wie jeder
                                andere Tag — aber er ist noch keine Zusage, und wer
                                den Kalender liest, muss das sehen. Die
                                Angebotsnummer steht dabei: „wessen Option?" ist die
                                nächste Frage.
                              */}
                              {t.optionsanfrage !== null && (
                                <>
                                  {' '}
                                  <span
                                    className={
                                      t.optionsstand === 'zugesagt'
                                        ? 'marke marke--gut'
                                        : 'marke marke--warn'
                                    }
                                  >
                                    {t.optionsstand === 'zugesagt' ? 'Option zugesagt' : 'Option'}
                                  </span>
                                  {t.angebotsnummer !== null && (
                                    <span className="feld__hilfe"> {t.angebotsnummer}</span>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="zahl">
                              {t.terminart === 'exklusiv'
                                ? '—'
                                : `${String(t.belegt)}/${String(t.hoechstteilnehmer)}`}
                            </td>
                            <td>
                              <span className="balkenbahn">
                                <span
                                  className={
                                    t.terminart === 'exklusiv'
                                      ? 'balken balken--exklusiv'
                                      : 'balken'
                                  }
                                  style={{ left: `${String(links)}%`, width: `${String(breite)}%` }}
                                >
                                  {/*
                                    DER BALKEN TRÄGT SEINE ZEIT (NFA-14).
                                    Eine Länge ist eine Anzeige und keine Aussage —
                                    sie ist nicht vorlesbar, verschwindet bei
                                    starker Vergrößerung und fehlt im Ausdruck.
                                  */}
                                  <span className="balken__text">{zeit}</span>
                                </span>
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        </div>
      )}

      {offenerTermin !== null && (
        <Terminfenster
          termin={offenerTermin}
          darfAbsagen={darfAbsagen}
          darfOptionBeantworten={darfOptionBeantworten}
          darfVereinbarungAendern={darfVereinbarungAendern}
          darfWartelisteSehen={darfWartelisteSehen}
          {...(beiTerminakte === undefined ? {} : { beiAkte: beiTerminakte })}
          beiSchliessen={() => {
            setzeOffenenTermin(null);
          }}
          beiAenderung={() => {
            void holen();
          }}
        />
      )}
    </Planung>
  );
}
